import { Controller, UseFormReturn } from "react-hook-form";
import Applicants from "@/model/interface/form/Applicants";
import ApiApplicationApplicant from "@/model/interface/api/ApiApplicationApplicant";
import React from "react";
import { Pencil, ChevronDown } from "lucide-react";
import CustomSelect from "@/components/shared/CustomSelect";
import { AddressState } from "@/model/interface/form/AddressState";
import DatePicker from "react-datepicker";
import { AddressStatus } from "@/enum/AddressStatus";

const inputClass = (hasError: boolean) =>
  `w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${hasError ? "border-red-400" : "border-gray-300"}`;

const ApplicantAddress = ({
  form,
  applicant,
  applicantType,
  addressStatus,
}: {
  form: UseFormReturn<Applicants>;
  applicant?: ApiApplicationApplicant;
  applicantType: "main-applicant" | "co-applicant";
  addressStatus: `${AddressStatus}`;
}) => {
  const { control, register, formState } = form;
  const address = addressStatus === "Active" ? "address" : "previousAddress";
  const applicantFormType = applicantType === "co-applicant" ? "coApplicant" : "mainApplicant";

  return (
    <div className="space-y-5">
      <h4 className="text-lg font-semibold text-gray-800">
        {addressStatus === "Active" ? "Current" : "Previous"} Address
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="md:col-span-2 relative">
          <h6 className="text-sm text-gray-500 mb-1">Street address</h6>
          <input
            type="text"
            {...register(`${applicantFormType}.${address}.address`, { value: applicant?.[address]?.address })}
            className={inputClass(!!formState.errors[applicantFormType]?.[address]?.address)}
            placeholder="Street address"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantFormType]?.[address]?.address && (
            <p className="mt-1 text-sm text-red-500">
              {formState.errors[applicantFormType]?.[address]?.address?.message}
            </p>
          )}
        </div>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Zip code</h6>
          <input
            type="text"
            {...register(`${applicantFormType}.${address}.zip`, { value: applicant?.[address]?.zip })}
            className={inputClass(!!formState.errors[applicantFormType]?.[address]?.zip)}
            placeholder="Zip code"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantFormType]?.[address]?.zip && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantFormType]?.[address]?.zip?.message}</p>
          )}
        </div>
      </div>
      <div className={`grid grid-cols-1 ${addressStatus === "Active" ? "md:grid-cols-3" : "md:grid-cols-2"} gap-5`}>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">City</h6>
          <input
            type="text"
            {...register(`${applicantFormType}.${address}.city`, { value: applicant?.[address]?.city })}
            className={inputClass(!!formState.errors[applicantFormType]?.[address]?.city)}
            placeholder="City"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantFormType]?.[address]?.city && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantFormType]?.[address]?.city?.message}</p>
          )}
        </div>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">State</h6>
          <Controller
            defaultValue={applicant?.[address]?.state}
            control={control}
            name={`${applicantFormType}.${address}.state`}
            render={({ field }) => (
              <CustomSelect
                value={AddressState.find((ms) => ms.value === field.value)!}
                onChange={(o: AddressState) => field.onChange(o.value)}
                options={AddressState}
                mapOptionToLabel={(o: AddressState) => o.label}
                mapOptionToValue={(o: AddressState) => o.value}
              />
            )}
          />
          <ChevronDown className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantFormType]?.[address]?.state && (
            <p className="mt-1 text-sm text-red-500">
              {formState.errors[applicantFormType]?.[address]?.state?.message}
            </p>
          )}
        </div>
        {addressStatus === "Active" && (
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">Address since</h6>
            <Controller
              defaultValue={
                applicant?.[address]?.addressHeldSince ? new Date(applicant?.[address]?.addressHeldSince) : undefined
              }
              control={control}
              name={`${applicantFormType}.${address}.addressHeldSince`}
              render={({ field }) => (
                <DatePicker
                  wrapperClassName="block w-full"
                  className={inputClass(!!formState.errors[applicantFormType]?.[address]?.addressHeldSince)}
                  placeholderText="Select approximate date"
                  onChange={(date) => field.onChange(date)}
                  selected={field.value}
                />
              )}
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantFormType]?.[address]?.addressHeldSince && (
              <p className="mt-1 text-sm text-red-500">
                {formState.errors[applicantFormType]?.[address]?.addressHeldSince?.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicantAddress;
